import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Collapse } from "@mui/material";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { debounce } from "lodash";
import cx from "classnames";

import { ReactComponent as Check } from "../../assets/icons/loadingGreen.svg";
import { ICONS, Icon } from "../Icons";
import { setAccordionStatus } from "../../store/actions/appConfig";
import { getAccordionStatuses } from "../../store/selectors/appConfig";
import { ContexualHelp } from "../Notifications";
import { StyledAccordionWrapper } from "./styled";

export const Accordion = ({
  id,
  title,
  children,
  showDefinition,
  isCompleted = false,
  subTitle,
  classes: extendedClasses,
  showHelperText,
  accordionRef,
  expandedDescription,
  expanded: externalExpanded, // Add external expanded prop
  onChange: externalOnChange, // Add external onChange prop
  ...others
}) => {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState("");

  // Use external expanded state if provided, otherwise use internal state
  const isExpanded = externalExpanded !== undefined ? externalExpanded : expanded;

  const transitionProps = {
    mountOnEnter: false,
    unmountOnExit: false
  };
  const accordionStatuses = useSelector(getAccordionStatuses);
  const statuses = JSON.parse(accordionStatuses);

  const [initialStatus, setInitialStatus] = useState(false);
  useEffect(() => {
    setInitialStatus(statuses[id]);
  }, []);

  const handleChange = panel => (event, newExpanded) => {
    if (externalOnChange) {
      // Use external onChange if provided
      externalOnChange(panel);
    } else {
      // Use internal state management
      setExpanded(newExpanded ? panel : false);
      if (newExpanded && isCompleted) {
        const updatedStatuses = {};
        updatedStatuses[id] = isCompleted;
        dispatch(setAccordionStatus(updatedStatuses));
      }
    }
  };

  const updateAccordionStatus = useCallback(
    debounce(updatedStatuses => {
      dispatch(setAccordionStatus(updatedStatuses));
    }, 100),
    []
  );

  useLayoutEffect(() => {
    const updatedStatuses = {};
    if (id === "isTaxDeclarationCompleted") {
      if ((initialStatus && isCompleted) || (expanded && isCompleted)) {
        updatedStatuses[id] = isCompleted;
      } else {
        updatedStatuses[id] = false;
      }
    } else {
      updatedStatuses[id] = isCompleted;
    }
    updateAccordionStatus(updatedStatuses);
  }, [initialStatus, id, isCompleted]);

  const byDefaultExpandedAccordion = [
    "documentLanding",
    "companyInformation",
    "stakeholderInformation",
    "productInformation",
    "codesBankUseReview",
    "checks",
    "companyInfoAccordion",
    "shareholdersDetails"
  ];

  const expandedDescriptionView = [
    "isSelectPackage",
    "preferences",
    "authorizations",
    "communication",
    "codesBankUse"
  ];

  return (
    <StyledAccordionWrapper className={"accordion"} {...others}>
      <MuiAccordion
        defaultExpanded={
          externalExpanded === undefined ? byDefaultExpandedAccordion.includes(id) : false
        }
        expanded={externalExpanded !== undefined ? isExpanded : undefined}
        onChange={handleChange(id)}
        square
        TransitionComponent={Collapse}
        TransitionProps={transitionProps}
        data-testid="accordion"
        open={isExpanded}
      >
        <MuiAccordionSummary
          expandIcon={<Icon name={ICONS.arrowDown} alt="arrow-down" className={"expandIcon"} />}
          id={id}
          ref={accordionRef}
        >
          <div className={cx("accordionSummaryWrapper", "customAccordionSummaryWrapper")}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span className="activePanel"></span>
              {isCompleted && statuses[id] && (
                <span data-testid="accordion-completed-status">
                  <Check size="16px" className={"success"} />
                </span>
              )}
              <div className="accordionTitle">
                <div className="title" data-testid="accordion-title">
                  {title}
                  {showHelperText && (
                    <ContexualHelp
                      title={showHelperText}
                      placement="right"
                      isDisableHoverListener={false}
                      dataTestId="accordion-help"
                    >
                      <HelpOutlineIcon className="helperIcon" />
                    </ContexualHelp>
                  )}
                </div>
                {subTitle && <p className="subTitle">{subTitle}</p>}
                {expandedDescriptionView.includes(expanded) && expandedDescription && (
                  <p className="expandedDescription">{expandedDescription}</p>
                )}
              </div>
            </div>
            <div>
              {(expanded === "isTaxDeclarationCompleted" ||
                expanded === "stakeholderTaxDeclarationSection") &&
                showDefinition}
            </div>
          </div>
        </MuiAccordionSummary>
        <MuiAccordionDetails
          classes={{ root: cx("accordionDetails", extendedClasses?.accordionDetails) }}
          data-testid="accordion-details"
        >
          {children}
        </MuiAccordionDetails>
      </MuiAccordion>
    </StyledAccordionWrapper>
  );
};

Accordion.propTypes = {
  /** Unique identifier for the accordion */
  id: PropTypes.string,

  /** Title to be displayed in the accordion header */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /** Content to be rendered within the accordion when expanded */
  children: PropTypes.node,

  /** JSX content to show definition related content */
  showDefinition: PropTypes.node,

  /** Indicates if the accordion section has been completed */
  isCompleted: PropTypes.bool,

  /** Subtitle to be displayed below the main title */
  subTitle: PropTypes.string,

  /** Optional classes for extending styling */
  classes: PropTypes.shape({
    accordionDetails: PropTypes.string
  }),

  /** Helper text that appears when hovering over the helper icon */
  showHelperText: PropTypes.node,

  /** Ref to be attached to the accordion element */
  accordionRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) })
  ]),

  /** Description text shown when the accordion is expanded */
  expandedDescription: PropTypes.string,

  /** Controls whether the accordion is expanded (controlled component) */
  expanded: PropTypes.bool,

  /** Callback fired when the expand state changes */
  onChange: PropTypes.func
};
